import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface TokenAuthContextType {
    isProcessingToken: boolean;
    tokenError: string | null;
    clearTokenError: () => void;
}

const TokenAuthContext = createContext<TokenAuthContextType | undefined>(undefined);

interface TokenAuthProviderProps {
    children: ReactNode;
    showErrorModal?: boolean;
}

export const TokenAuthProvider: React.FC<TokenAuthProviderProps> = ({ children, showErrorModal = true }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithToken, isAuthenticated } = useAuth();
    const [isProcessingToken, setIsProcessingToken] = useState(false);
    const [tokenError, setTokenError] = useState<string | null>(null);

    // Use ref to track if token has been processed to prevent duplicate calls
    const processedTokenRef = useRef<string | null>(null);

    useEffect(() => {
        const handleTokenLogin = async () => {
            // Get token from URL
            const token = searchParams.get('token');

            // Skip if:
            // - No token in URL
            // - Already authenticated
            // - Currently processing a token
            // - This exact token was already processed
            if (!token || isAuthenticated || isProcessingToken || processedTokenRef.current === token) {
                return;
            }

            console.log('[TokenAuthProvider] Token detected in URL, processing...');

            // Mark this token as being processed
            processedTokenRef.current = token;
            setIsProcessingToken(true);
            setTokenError(null);

            try {
                console.log('[TokenAuthProvider] Attempting login with token...');
                await loginWithToken(token);
                console.log('[TokenAuthProvider] Token login successful');

                // Remove token from URL immediately after successful login
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('token');

                // Replace URL without the token
                const newUrl = location.pathname + (newSearchParams.toString() ? `?${newSearchParams.toString()}` : '');
                window.history.replaceState({}, '', newUrl);

                setIsProcessingToken(false);

                // Optional: Navigate to dashboard or intended page after successful login
                // navigate('/dashboard', { replace: true });

            } catch (err: any) {
                console.error('[TokenAuthProvider] Token login failed:', err);
                setTokenError(err.message || 'Authentication failed. Please try again from your mobile app.');
                setIsProcessingToken(false);

                // Remove token from URL on error
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('token');
                const newUrl = location.pathname + (newSearchParams.toString() ? `?${newSearchParams.toString()}` : '');
                window.history.replaceState({}, '', newUrl);
            }
        };

        handleTokenLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]); // Only depend on searchParams to avoid unnecessary re-renders

    const clearTokenError = useCallback(() => {
        setTokenError(null);
        processedTokenRef.current = null; // Reset processed token ref when error is cleared
    }, []);

    // Show loading overlay while processing token (portal only)
    if (isProcessingToken && showErrorModal) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Logging you in...</h2>
                    <p className="text-gray-600">Please wait while we authenticate your account</p>
                </div>
            </div>
        );
    }

    // Show error modal overlay if token login failed (portal only)
    if (tokenError && showErrorModal) {
        return (
            <>
                {/* Render children in background */}
                <TokenAuthContext.Provider value={{ isProcessingToken, tokenError, clearTokenError }}>
                    {children}
                </TokenAuthContext.Provider>

                {/* Error overlay */}
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                        <div className="text-center mb-6">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <svg
                                    className="h-6 w-6 text-red-600"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h2>
                            <p className="text-gray-600 mb-6">{tokenError}</p>
                        </div>
                        <div className="space-y-3">
                            <button
                                onClick={() => {
                                    clearTokenError();
                                    navigate('/login', { replace: true });
                                }}
                                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition duration-200"
                            >
                                Go to Login
                            </button>
                            <p className="text-sm text-gray-500 text-center">
                                Open the Move Club app on your phone and try again
                            </p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <TokenAuthContext.Provider value={{ isProcessingToken, tokenError, clearTokenError }}>
            {children}
        </TokenAuthContext.Provider>
    );
};

export const useTokenAuth = (): TokenAuthContextType => {
    const context = useContext(TokenAuthContext);
    if (!context) {
        throw new Error('useTokenAuth must be used within TokenAuthProvider');
    }
    return context;
};